import { useCallback, useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { consumeDeal, getDealStatus } from '@/services/dealService';
import type { DealStatus } from '@/types/deals';
import type { ExtendedJWTClaims } from '@/hooks/useJWTClaims';

/**
 * SubscriptionStatus is the canonical billing-state object exposed by
 * AuthContext. After Slice I3 (2026-04-16) the legacy token model is gone:
 * the entire schema is sourced from organization_deals via getDealStatus().
 *
 * The legacy "tokens*" / "tier*" / "status" fields are kept as aliases of
 * the deals fields so existing consumers (UpgradePrompt, SubscriptionBanner,
 * etc.) don't have to change their reads. Once those consumers are updated
 * the alias fields can be dropped in a follow-up; the alias path adds zero
 * runtime cost beyond a few extra object properties.
 */
export interface SubscriptionStatus {
  // Deals model — canonical fields
  planKey: string;
  planDisplayName: string;
  dealsPerMonth: number;
  /**
   * Admin-granted one-time bonus for the current billing period (Slice H).
   * Added to dealsPerMonth to form the effective ceiling.
   */
  bonusDealsThisPeriod: number;
  dealsUsed: number;
  dealsRemaining: number;
  subscriptionStatus: 'active' | 'past_due' | 'canceled' | 'trialing' | 'inactive';
  isUnlimited: boolean;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  daysUntilRenewal: number | null;
  // Legacy aliases — same data, older field names. Kept until downstream
  // consumers are migrated; safe to drop once no caller reads tier* / tokens*
  // / status.
  tier: string;
  tierDisplayName: string;
  tokensAllocated: number;
  tokensUsed: number;
  tokensRemaining: number;
  status: 'active' | 'suspended' | 'cancelled' | 'trial';
}

interface UseSubscriptionLoaderResult {
  subscriptionStatus: SubscriptionStatus | null;
  consumeTCTokens: (amount?: number, transactionId?: number) => Promise<boolean>;
  refreshSubscription: () => Promise<void>;
}

function mapDealStatusToSubscriptionStatus(dealStatus: DealStatus): SubscriptionStatus {
  // The deals model has no concept of "unlimited"; ceilings are always finite.
  // Kept on the type for forward-compat, hard-wired false until product asks
  // for an enterprise unlimited tier.
  const isUnlimited = false;

  let legacyStatus: 'active' | 'suspended' | 'cancelled' | 'trial' = 'active';
  if (dealStatus.subscriptionStatus === 'trialing') legacyStatus = 'trial';
  else if (dealStatus.subscriptionStatus === 'canceled') legacyStatus = 'cancelled';
  else if (dealStatus.subscriptionStatus === 'inactive' || dealStatus.subscriptionStatus === 'past_due') {
    legacyStatus = 'suspended';
  }

  const effectiveCeiling = dealStatus.dealsPerMonth + dealStatus.bonusDealsThisPeriod;

  return {
    planKey: dealStatus.planKey,
    planDisplayName: dealStatus.planDisplayName,
    dealsPerMonth: dealStatus.dealsPerMonth,
    bonusDealsThisPeriod: dealStatus.bonusDealsThisPeriod,
    dealsUsed: dealStatus.dealsUsedThisPeriod,
    dealsRemaining: dealStatus.dealsRemaining,
    subscriptionStatus: dealStatus.subscriptionStatus as SubscriptionStatus['subscriptionStatus'],
    isUnlimited,
    currentPeriodEnd: dealStatus.currentPeriodEnd,
    cancelAtPeriodEnd: dealStatus.cancelAtPeriodEnd,
    daysUntilRenewal: dealStatus.daysUntilRenewal,
    // Legacy aliases — point at the EFFECTIVE ceiling so any "X / Y" UI
    // includes the admin bonus.
    tier: dealStatus.planKey || 'Free Trial',
    tierDisplayName: dealStatus.planDisplayName || 'Free Trial',
    tokensAllocated: effectiveCeiling,
    tokensUsed: dealStatus.dealsUsedThisPeriod,
    tokensRemaining: dealStatus.dealsRemaining,
    status: legacyStatus,
  };
}

export function useSubscriptionLoader(
  user: User | null,
  claims: ExtendedJWTClaims | null
): UseSubscriptionLoaderResult {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const subscriptionLoadedRef = useRef<string | null>(null);

  const refreshSubscription = useCallback(async () => {
    console.log('[AuthContext] Manually refreshing subscription');
    try {
      const dealStatusResult = await getDealStatus();
      setSubscriptionStatus(mapDealStatusToSubscriptionStatus(dealStatusResult));
    } catch (err) {
      console.error('[AuthContext] Error refreshing subscription:', err);
    }
  }, []);

  const consumeTCTokens = useCallback(
    async (amount: number = 1, transactionId?: number): Promise<boolean> => {
      if (!subscriptionStatus) {
        console.error('No subscription status found');
        return false;
      }

      if (!subscriptionStatus.isUnlimited && subscriptionStatus.dealsRemaining < amount) {
        console.error('Insufficient deals');
        return false;
      }

      try {
        // Pass organization_id explicitly when known. The DB-side
        // consume_deal RPC will fall back to get_current_org_id() if we
        // pass undefined (post-2026-04-18 hotfix), but threading the
        // value from the JWT claims is the safer normal path — it makes
        // the call self-describing and avoids any future PostgREST
        // schema-cache lag from breaking transaction creation.
        await consumeDeal(transactionId, claims?.organization_id ?? undefined);
        await refreshSubscription();
        return true;
      } catch (err) {
        console.error('Exception consuming deals:', err);
        return false;
      }
    },
    [subscriptionStatus, refreshSubscription, claims?.organization_id]
  );

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user?.id) {
        setSubscriptionStatus(null);
        subscriptionLoadedRef.current = null;
        return;
      }

      if (!claims) {
        return;
      }

      const organizationIdFromClaims = claims.organization_id;
      const loadKey = `org-${organizationIdFromClaims ?? user.id}`;

      if (subscriptionLoadedRef.current === loadKey) {
        console.log('[AuthContext] Subscription already loaded for:', loadKey);
        return;
      }

      console.log('[AuthContext] Loading subscription status for:', loadKey);
      subscriptionLoadedRef.current = loadKey;

      try {
        const dealStatusResult = await getDealStatus();
        setSubscriptionStatus(mapDealStatusToSubscriptionStatus(dealStatusResult));
      } catch (err) {
        console.error('[AuthContext] Error loading subscription status:', err);
        setSubscriptionStatus(null);
      }
    };

    void loadSubscription();
  }, [user, claims]);

  return {
    subscriptionStatus,
    consumeTCTokens,
    refreshSubscription,
  };
}
